'use client'

import { useEffect, useCallback, useRef } from 'react'
import { useParams } from 'next/navigation'
import { useDispatch, useSelector } from 'react-redux'
import { IRedux } from '@/_store/types'
import { authActions } from '@/_store/auth/auth.slice'
import { activeBrandStore } from '@/_store/brand/active-brand.store'
import mikaService from '@/_service/mika.service'
import { FEATURED_BRANDS } from '@/_utils/Constants'
import { resolveBrandId } from '@/_lib/brand-href'

/**
 * Hook to manage brand-specific authentication
 * 
 * This hook should be used in brand page layouts to:
 * 1. Set the active brand ID in the store (for axios interceptor)
 * 2. Fetch brand-specific token if user is authenticated but brand token is missing
 * 3. Clean up active brand on unmount
 * 
 * Usage:
 * ```tsx
 * export default function BrandLayout({ children }) {
 *   useBrandAuth()
 *   return <>{children}</>
 * }
 * ```
 */
export function useBrandAuth() {
  const params = useParams()
  const dispatch = useDispatch()
  const isFetchingRef = useRef(false)

  const shipperId = resolveBrandId(
    String(params?.brandSlug || params?.id || ''),
  )
  const isYaponamama = shipperId === FEATURED_BRANDS['yaponamama']

  const { user, tokens } = useSelector((state: IRedux) => state.auth)
  const brandToken = tokens[shipperId]?.access_token
  const hasGlobalToken = Boolean(user?.access_token)

  // Set active brand on mount, clear on unmount
  useEffect(() => {
    if (shipperId) {
      activeBrandStore.setShipperId(shipperId)
    }

    return () => {
      activeBrandStore.setShipperId(null)
    }
  }, [shipperId])

  // Fetch brand token if needed
  const fetchBrandToken = useCallback(async () => {
    // Skip if:
    // - No shipperId
    // - Already fetching
    // - Already has brand token
    // - No global token to exchange
    // - Is yaponamama (uses global token)
    if (
      !shipperId ||
      isFetchingRef.current ||
      brandToken ||
      !hasGlobalToken ||
      isYaponamama
    ) {
      return
    }

    isFetchingRef.current = true

    try {
      const response = await mikaService.refresh({
        shipper_id: shipperId,
        token: user!.access_token,
      })

      if (response.data?.access_token) {
        dispatch(
          authActions.setBrandToken({
            shipperId,
            access_token: response.data.access_token,
            refresh_token: response.data.refresh_token,
            client_id: response.data.id,
          })
        )
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch brand token:', error)
      }
    } finally {
      isFetchingRef.current = false
    }
  }, [shipperId, brandToken, hasGlobalToken, isYaponamama, user, dispatch])

  // Fetch brand token when conditions are met
  useEffect(() => {
    fetchBrandToken()
  }, [fetchBrandToken])

  return {
    shipperId,
    isAuthenticated: Boolean(brandToken || (isYaponamama && hasGlobalToken)),
    brandToken,
  }
}

export default useBrandAuth
